import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import DreamMarque from './common/DreamMarque';
import { ReactComponent as DreamWordmark } from '../icons/brand/dream-wordmark.svg';
import PublicNoticePage from './common/PublicNoticePage';
import { TickIcon } from './onboardingWeb/wizardIcons';
import { WAITLIST_URL } from './JoinPage';
import { sendOnboardingLinkEvent } from '../lib/onboardingLinkEvents';
import 'flag-icons/css/flag-icons.min.css';
import './referral-redirect-page.scss';

type SupportedLanguage = 'en' | 'fr' | 'de' | 'es' | 'it';
type ReferralLinkStatus = 'checking' | 'ok' | 'unavailable' | 'invalid';

interface ReferralRedirectCopy {
  title: string;
  body: string;
  fallback: string;
  button: string;
  runOutTitle: string;
  runOutBody: string;
  waitlistButton: string;
  languageLabel: string;
  whatIsTitle: string;
  whatIsBody: string;
  whatIsPoints: string[];
}

interface ReferralRedirectPageProps {
  redirect?: (destination: string) => void;
}

const REDIRECT_DELAY_MS = 5000;
const REFERRAL_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{8}$/;
const ACQUISITION_CODE_PATTERN = /^[A-Z0-9]{4,32}$/;
const INVALID_INVITATION_TITLE = 'Invalid Dream invitation';
const LANGUAGE_OPTIONS: Record<SupportedLanguage, string> = {
  en: 'English',
  fr: 'Français',
  de: 'Deutsch',
  es: 'Español',
  it: 'Italiano',
};
const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  en: 'gb',
  fr: 'fr',
  de: 'de',
  es: 'es',
  it: 'it',
};
const SUPPORTED_LANGUAGES = Object.keys(
  LANGUAGE_OPTIONS,
) as SupportedLanguage[];
const COPY: Record<SupportedLanguage, ReferralRedirectCopy> = {
  en: {
    title: 'You’re opening a Dream invitation',
    body: 'We’re sending you to WhatsApp to get your AI Secretary.',
    fallback: 'If you aren’t redirected automatically, tap the button above.',
    button: 'Open WhatsApp now',
    runOutTitle: 'This invite link has run out',
    runOutBody: 'Leave your details to be added to the waitlist.',
    waitlistButton: 'Join the waitlist',
    languageLabel: 'Language',
    whatIsTitle: 'What is Dream',
    whatIsBody:
      'Your AI Secretary for emails, meetings, travel and personal admin—secure, private, backed by real secretaries.',
    whatIsPoints: [
      'In WhatsApp, where you already message',
      'Real secretaries when a job needs a person',
      'Secure, private, built for business',
      'Your free invitation to join',
    ],
  },
  fr: {
    title: 'Tu ouvres une invitation Dream',
    body: 'Nous te redirigeons vers WhatsApp pour obtenir ta Secrétaire IA.',
    fallback:
      'Si tu n’es pas redirigé automatiquement, appuie sur le bouton ci-dessus.',
    button: 'Ouvrir WhatsApp',
    runOutTitle: 'Ce lien d’invitation est épuisé',
    runOutBody: 'Laisse tes coordonnées pour être ajouté à la liste d’attente.',
    waitlistButton: 'Rejoindre la liste d’attente',
    languageLabel: 'Langue',
    whatIsTitle: 'Qu’est-ce que Dream',
    whatIsBody:
      'Ta Secrétaire IA pour les e-mails, réunions, voyages et tâches administratives personnelles — sécurisée, confidentielle, avec de vraies secrétaires.',
    whatIsPoints: [
      'Dans WhatsApp, là où tu échanges déjà',
      'De vraies secrétaires quand une tâche demande une personne',
      'Sécurisé, confidentiel, conçu pour les professionnels',
      'Ton invitation gratuite',
    ],
  },
  de: {
    title: 'Du öffnest eine Einladung zu Dream',
    body: 'Du wirst zu WhatsApp weitergeleitet, um deine KI-Sekretärin einzurichten.',
    fallback:
      'Falls du nicht automatisch weitergeleitet wirst, tippe auf den Button oben.',
    button: 'WhatsApp jetzt öffnen',
    runOutTitle: 'Dieser Einladungslink ist aufgebraucht',
    runOutBody: 'Hinterlasse deine Daten, um auf die Warteliste zu kommen.',
    waitlistButton: 'Auf die Warteliste',
    languageLabel: 'Sprache',
    whatIsTitle: 'Was ist Dream',
    whatIsBody:
      'Deine KI-Sekretärin für E-Mails, Termine, Reisen und persönliche Organisation – sicher, vertraulich, unterstützt von echten Sekretärinnen.',
    whatIsPoints: [
      'In WhatsApp, wo du ohnehin schreibst',
      'Echte Sekretärinnen, wenn eine Aufgabe einen Menschen braucht',
      'Sicher, vertraulich, fürs Business gemacht',
      'Deine kostenlose Einladung',
    ],
  },
  es: {
    title: 'Estás abriendo una invitación a Dream',
    body: 'Te enviaremos a WhatsApp para configurar tu Secretaria de IA.',
    fallback: 'Si no te redirigimos automáticamente, toca el botón de arriba.',
    button: 'Abrir WhatsApp',
    runOutTitle: 'Este enlace de invitación se ha agotado',
    runOutBody: 'Deja tus datos para unirte a la lista de espera.',
    waitlistButton: 'Unirme a la lista de espera',
    languageLabel: 'Idioma',
    whatIsTitle: '¿Qué es Dream?',
    whatIsBody:
      'Tu Secretaria de IA para correos, reuniones, viajes y gestiones personales: segura, privada y con secretarias reales.',
    whatIsPoints: [
      'En WhatsApp, donde ya escribes',
      'Secretarias reales cuando una tarea necesita a una persona',
      'Seguro, privado y pensado para empresas',
      'Tu invitación gratuita',
    ],
  },
  it: {
    title: 'Stai aprendo un invito a Dream',
    body: 'Ti stiamo reindirizzando a WhatsApp per configurare la tua Segretaria IA.',
    fallback:
      'Se non vieni reindirizzato automaticamente, tocca il pulsante qui sopra.',
    button: 'Apri WhatsApp',
    runOutTitle: 'Questo link d’invito è esaurito',
    runOutBody: 'Lascia i tuoi dati per entrare nella lista d’attesa.',
    waitlistButton: 'Entra nella lista d’attesa',
    languageLabel: 'Lingua',
    whatIsTitle: 'Cos’è Dream',
    whatIsBody:
      'La tua Segretaria IA per email, riunioni, viaggi e faccende personali: sicura, riservata, con vere segretarie.',
    whatIsPoints: [
      'Su WhatsApp, dove già scrivi',
      'Vere segretarie quando serve una persona',
      'Sicuro, riservato, pensato per il business',
      'Il tuo invito gratuito',
    ],
  },
};

const supportedBaseLanguage = (
  language: string | null,
): SupportedLanguage | null => {
  const baseLanguage = language?.toLowerCase().split('-')[0];
  return baseLanguage && baseLanguage in LANGUAGE_OPTIONS
    ? (baseLanguage as SupportedLanguage)
    : null;
};

const recipientLanguage = (requestedLanguage: string | null) => {
  const requested = supportedBaseLanguage(requestedLanguage);
  if (requested) return requested;

  for (const acceptedLanguage of [
    ...window.navigator.languages,
    window.navigator.language,
  ]) {
    const accepted = supportedBaseLanguage(acceptedLanguage);
    if (accepted) return accepted;
  }

  return 'en';
};

const replaceLocation = (destination: string) =>
  window.location.replace(destination);

const isValidInvitationCode = (code: string) =>
  REFERRAL_CODE_PATTERN.test(code) ||
  (code.length !== 8 && ACQUISITION_CODE_PATTERN.test(code));

const ReferralRedirectPage: React.FC<ReferralRedirectPageProps> = ({
  redirect = replaceLocation,
}) => {
  const { code = '' } = useParams();
  const [searchParams] = useSearchParams();
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [linkStatus, setLinkStatus] = useState<ReferralLinkStatus>('checking');
  const pageViewSentForCode = useRef<string | null>(null);
  const tapSentForCode = useRef<string | null>(null);
  const languagePickerRef = useRef<HTMLDivElement>(null);
  const languageButtonRef = useRef<HTMLButtonElement>(null);
  const languageMenuId = useId();
  const aboutTitleId = useId();
  const normalizedCode = code.trim().toUpperCase();
  const language = recipientLanguage(searchParams.get('lang'));
  const copy = COPY[language];
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const destination = `${apiBase}/referral-short-link/${encodeURIComponent(
    normalizedCode,
  )}?lang=${language}`;
  const statusUrl = `${apiBase}/referral-short-link/${encodeURIComponent(
    normalizedCode,
  )}/status`;
  const isValidCode = isValidInvitationCode(normalizedCode);
  const sendWhatsAppTap = useCallback(() => {
    if (tapSentForCode.current === normalizedCode) return;
    tapSentForCode.current = normalizedCode;
    sendOnboardingLinkEvent(normalizedCode, 'whatsapp_tap', 'app_ref');
  }, [normalizedCode]);
  const isInvalid = !isValidCode || linkStatus === 'invalid';
  const documentLanguage = isInvalid ? 'en' : language;
  const documentTitle = isInvalid
    ? INVALID_INVITATION_TITLE
    : linkStatus === 'unavailable'
      ? copy.runOutTitle
      : copy.title;

  useEffect(() => {
    if (!languageMenuOpen) return undefined;

    const handlePointerDown = (event: PointerEvent) => {
      if (
        languagePickerRef.current &&
        !languagePickerRef.current.contains(event.target as Node)
      ) {
        setLanguageMenuOpen(false);
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setLanguageMenuOpen(false);
      languageButtonRef.current?.focus();
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [languageMenuOpen]);

  useEffect(() => {
    const previousLanguage = document.documentElement.lang;
    const previousTitle = document.title;
    document.documentElement.lang = documentLanguage;
    document.title = documentTitle;
    return () => {
      document.documentElement.lang = previousLanguage;
      document.title = previousTitle;
    };
  }, [documentLanguage, documentTitle]);

  useEffect(() => {
    if (!isValidCode) return undefined;

    let active = true;
    setLinkStatus('checking');

    const checkStatus = async () => {
      try {
        const response = await fetch(statusUrl);
        if (response.status >= 500) {
          if (active) setLinkStatus('ok');
          return;
        }

        const result = (await response.json()) as { status?: string };
        if (!active) return;

        if (result.status === 'invalid') {
          setLinkStatus('invalid');
        } else if (result.status === 'unavailable') {
          setLinkStatus('unavailable');
        } else {
          setLinkStatus('ok');
        }
      } catch {
        if (active) setLinkStatus('ok');
      }
    };

    void checkStatus();
    return () => {
      active = false;
    };
  }, [isValidCode, statusUrl]);

  useEffect(() => {
    if (!isValidCode || linkStatus !== 'ok') return undefined;

    const timeout = window.setTimeout(() => {
      sendWhatsAppTap();
      redirect(destination);
    }, REDIRECT_DELAY_MS);
    return () => window.clearTimeout(timeout);
  }, [destination, isValidCode, linkStatus, redirect, sendWhatsAppTap]);

  useEffect(() => {
    if (
      !isValidCode ||
      linkStatus !== 'ok' ||
      pageViewSentForCode.current === normalizedCode
    )
      return;
    pageViewSentForCode.current = normalizedCode;
    sendOnboardingLinkEvent(normalizedCode, 'page_view', 'app_ref');
  }, [isValidCode, linkStatus, normalizedCode]);

  if (isInvalid) {
    return (
      <PublicNoticePage
        className='referral-redirect--invalid'
        title={INVALID_INVITATION_TITLE}
      />
    );
  }

  return (
    <main className='referral-redirect'>
      <header className='referral-redirect__header'>
        <div className='referral-redirect__logo' aria-hidden='true'>
          <DreamMarque className='referral-redirect__marque' />
          <DreamWordmark className='referral-redirect__wordmark' />
        </div>
        <div
          className='referral-redirect__language-picker'
          ref={languagePickerRef}
        >
          <button
            ref={languageButtonRef}
            type='button'
            className='referral-redirect__language-button'
            aria-label={copy.languageLabel}
            aria-haspopup='menu'
            aria-expanded={languageMenuOpen}
            aria-controls={languageMenuId}
            onClick={() => setLanguageMenuOpen((open) => !open)}
          >
            <span
              className={`referral-redirect__flag fi fi-${LANGUAGE_FLAGS[language]}`}
              aria-hidden='true'
            />
            <span>{language.toUpperCase()}</span>
            <svg
              className='referral-redirect__language-chevron'
              width='12'
              height='12'
              viewBox='0 0 12 12'
              aria-hidden='true'
            >
              <path
                d='M2.5 4.5L6 8l3.5-3.5'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
          </button>
          {languageMenuOpen && (
            <div
              id={languageMenuId}
              className='referral-redirect__language-menu'
              role='menu'
              aria-label={copy.languageLabel}
            >
              {SUPPORTED_LANGUAGES.map((option) => (
                <Link
                  key={option}
                  className='referral-redirect__language-option'
                  to={`/ref/${normalizedCode}?lang=${option}`}
                  lang={option}
                  role='menuitem'
                  aria-current={option === language ? 'page' : undefined}
                  onClick={() => setLanguageMenuOpen(false)}
                >
                  <span
                    className={`referral-redirect__flag fi fi-${LANGUAGE_FLAGS[option]}`}
                    aria-hidden='true'
                  />
                  <span>{LANGUAGE_OPTIONS[option]}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </header>
      <section className='referral-redirect__card'>
        <div className='referral-redirect__invitation'>
          {linkStatus === 'unavailable' ? (
            <>
              <h1>{copy.runOutTitle}</h1>
              <p className='referral-redirect__body'>{copy.runOutBody}</p>
              <a
                className='referral-redirect__button referral-redirect__button--waitlist'
                href={WAITLIST_URL}
              >
                {copy.waitlistButton}
              </a>
            </>
          ) : (
            <>
              <h1>{copy.title}</h1>
              <p className='referral-redirect__body'>{copy.body}</p>
              <div className='referral-redirect__progress' aria-hidden='true' />
              <a
                className='referral-redirect__button'
                href={destination}
                onClick={sendWhatsAppTap}
              >
                {copy.button}
              </a>
              <p className='referral-redirect__fallback'>{copy.fallback}</p>
            </>
          )}
        </div>
        <section
          className='referral-redirect__about'
          aria-labelledby={aboutTitleId}
        >
          <h2 id={aboutTitleId}>{copy.whatIsTitle}</h2>
          <p className='referral-redirect__body'>{copy.whatIsBody}</p>
          <ul className='referral-redirect__about-points'>
            {copy.whatIsPoints.map((point) => (
              <li className='referral-redirect__about-point' key={point}>
                <span
                  className='referral-redirect__about-icon'
                  aria-hidden='true'
                >
                  <TickIcon />
                </span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </section>
      </section>
    </main>
  );
};

export default ReferralRedirectPage;
